import React, { MutableRefObject, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { EntryConfirmation } from '@/components/checkout/EntryConfirmation';
import { EntryConfirmationTimeout } from '@/components/checkout/EntryConfirmationTimeout';
import { lobbyId } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { usePushPrimerEligibility } from '@/feature/push-primer/hooks/use-push-primer-eligibility';
import { useRateApp } from '@/feature/rate-app/hooks/use-rate-app';
import { Product } from '@/hooks/use-jurisdiction';
import { useParentGestureLock } from '@/hooks/use-parent-gesture-lock';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useWalletStore } from '@/hooks/use-wallet';
import { SubmitEntryNavigationProp } from '@/navigation/pickem/types';

import { EntryConfirmationButtons } from '../components/EntryConfirmationButtons';
import { SweepsPromoBanner } from '../components/SweepsPromoBanner';
import { useBetslipStore } from '../hooks/use-betslip-store';
import { useEntryRows } from '../hooks/use-entry-rows';
import { PlaceEntryStatus } from '../hooks/use-place-entry';
import { BetslipPick } from '../types';

type Props = {
    entry: EntryInfoFragment | undefined;
    operationStatus: PlaceEntryStatus;
    isMysteryMode: boolean;
    isFreeEntry: boolean;
    isRaceToPlaceEntry: boolean;
    isTimeout: boolean;
    keepBetDataWhenScreenRemoved: MutableRefObject<boolean>;
    wheelExited: boolean;
    picks: BetslipPick[];
    multiplier: string;
    onSpinAgain: (newAmount: number) => void;
};

export const SubmitEntryConfirmation = memo(
    ({
        entry,
        operationStatus,
        isMysteryMode,
        isFreeEntry,
        isRaceToPlaceEntry,
        isTimeout,
        keepBetDataWhenScreenRemoved,
        wheelExited,
        picks,
        multiplier,
        onSpinAgain,
    }: Props) => {
        const navigation = useNavigation<SubmitEntryNavigationProp>();
        const { t } = useTranslation(['betslip_pickem', 'common']);

        const pushPrimerEligibility = usePushPrimerEligibility();
        const { maybeShowAppRateAfterEntryPlaced } = useRateApp();
        const removeAllPicks = useBetslipStore(state => state.actions.clearBetslip);
        const clearRefferer = useBetslipStore(state => state.actions.clearRefferer);
        const realMoneyTotal = useWalletStore(state => state.realMoneyTotal);
        const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

        const { title, rows } = useEntryRows({
            entry,
            isMysteryMode,
            isFreeEntry,
            isRaceToPlaceEntry,
            picksCount: picks.length,
            multiplier,
        });

        const spinAgainAmount = Math.min(entry?.initialAmount ?? 1, realMoneyTotal);

        const handleSpinAgainPress = useCallback(() => {
            setIsConfirmationVisible(false);
        }, []);

        const handleReusePress = useCallback(() => {
            keepBetDataWhenScreenRemoved.current = true;
        }, [keepBetDataWhenScreenRemoved]);

        useParentGestureLock(isMysteryMode ? !isConfirmationVisible : operationStatus === 'loading');
        usePreventAndroidBackNavigation();

        // mystery: show after the wheel exits; hide when a new spin starts.
        useEffect(() => {
            if (!isMysteryMode) return;
            setIsConfirmationVisible(wheelExited);
        }, [wheelExited, isMysteryMode]);

        const clearBetslip = useCallback(() => {
            // race-to-place picks don't go into the betslip, so only the referrer is cleared
            if (isRaceToPlaceEntry) {
                clearRefferer();
                return;
            }
            removeAllPicks();
        }, [isRaceToPlaceEntry, removeAllPicks, clearRefferer]);

        // cleanup pickslip on any navigation away from this screen,
        // except when reusing the lineup or when there's an error (keep the picks so the user can try submitting again)
        useEffect(() => {
            const unsubscribe = navigation.addListener('beforeRemove', () => {
                if (keepBetDataWhenScreenRemoved.current) return;
                clearBetslip();
                maybeShowAppRateAfterEntryPlaced({ delay: 2000 });
            });
            return unsubscribe;
        }, [navigation, clearBetslip, maybeShowAppRateAfterEntryPlaced, keepBetDataWhenScreenRemoved]);

        const onClosePress = useCallback(() => {
            if (!isTimeout && pushPrimerEligibility) {
                // navigate keeps SubmitEntry in the stack so beforeRemove won't fire — clean up explicitly.
                clearBetslip();
                maybeShowAppRateAfterEntryPlaced({ delay: 2000 });
                navigation.navigate('PushPrimer');
            } else {
                navigation.popTo('PickemHome', {
                    screen: 'Lobby',
                    params: { screen: 'LobbyScreen', params: { league: lobbyId } },
                });
            }
        }, [isTimeout, maybeShowAppRateAfterEntryPlaced, navigation, clearBetslip, pushPrimerEligibility]);

        const handleExitComplete = useCallback(() => {
            if (isMysteryMode) {
                onSpinAgain(spinAgainAmount);
            }
        }, [isMysteryMode, onSpinAgain, spinAgainAmount]);

        if (isTimeout) {
            return (
                <EntryConfirmationTimeout
                    onClose={onClosePress}
                    title={t('processing_title')}
                    description={t('processing_description')}
                    buttonLabel={t('common:dismiss')}
                />
            );
        }

        return (
            <EntryConfirmation
                onClose={onClosePress}
                animated={isMysteryMode}
                visible={isConfirmationVisible}
                onExitComplete={handleExitComplete}
                title={title}
                product={Product.Pickem}
                rows={rows}
                buttons={
                    <EntryConfirmationButtons
                        entry={entry}
                        isMysteryMode={isMysteryMode}
                        isRaceToPlaceEntry={isRaceToPlaceEntry}
                        operationStatus={operationStatus}
                        spinAgainAmount={spinAgainAmount}
                        onSpinAgainPress={handleSpinAgainPress}
                        onReusePress={handleReusePress}
                    />
                }
                banner={<SweepsPromoBanner />}
            />
        );
    }
);
